/**
 * Every URL the app answers, as typed routes. Deep links follow FR-PROJ-059: /p/<project>/<module>/<tab>/<item>,
 * with selections as ?sel=<element>. Parsing and building live together so they cannot drift apart.
 */

export const MODULE_IDS = ["site", "design", "render", "studio"] as const;
export type ModuleId = (typeof MODULE_IDS)[number];

/**
 * First-run steps after sign-in (FR-ADMIN-002 passkey offer, FR-ADMIN-009 organisation setup, FR-PROJ-087), plus
 * "terms": accepting the terms version and confirming 18+ (FR-ADMIN-001), which hosted sign-in cannot collect.
 * Passkeys are now enrolled inside hosted sign-in, so "passkey" only redirects onward.
 */
export const ONBOARDING_STEPS = ["passkey", "organisation", "workspace", "terms"] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export const SETTINGS_SECTIONS = [
  "profile",
  "security",
  "notifications",
  "organisation",
  "members",
  "teams",
  "fonts",
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/** Staff console v0 (FR-ADMIN-051, 052). Internal-only in production; reachable in the mock to complete the flow. */
export const STAFF_SECTIONS = ["requests", "organisations"] as const;
export type StaffSection = (typeof STAFF_SECTIONS)[number];

export type Route =
  | { name: "landing" }
  | { name: "signIn" }
  | { name: "signUp" }
  /** Hosted sign-in returns here with ?code=…&state=… (WorkOS AuthKit through the identity service). */
  | { name: "authCallback" }
  | { name: "requestAccess" }
  /** The signed-in organisation has no plan, so nothing is usable until staff activate it (D-034). */
  | { name: "notActivated" }
  | { name: "onboarding"; step: OnboardingStep }
  | { name: "invite"; token: string }
  | { name: "share"; token: string }
  | { name: "settings"; section: SettingsSection }
  | { name: "staff"; section: StaffSection }
  | { name: "projects" }
  | { name: "newProject" }
  | { name: "overview"; projectId: string }
  | { name: "files"; projectId: string }
  | { name: "module"; projectId: string; module: ModuleId; tab?: string; item?: string }
  | { name: "draft"; projectId: string; drawingId?: string }
  | { name: "notFound"; path: string };

const PROJECT_PREFIX = "p";
const DRAFT_TAB = "draft";

export function isModuleId(value: string): value is ModuleId {
  return (MODULE_IDS as readonly string[]).includes(value);
}

function segments(pathname: string): string[] {
  return pathname
    .split("/")
    .filter((part) => part !== "")
    .map((part) => decodeURIComponent(part));
}

function oneOf<T extends string>(values: readonly T[], value: string | undefined): value is T {
  return value !== undefined && (values as readonly string[]).includes(value);
}

/** Routes outside projects: identity, onboarding, sharing, settings and the staff console. */
function parseOther(parts: readonly string[]): Route | undefined {
  const [first, second, ...rest] = parts;
  if (rest.length > 0) return undefined;
  if (second === undefined) {
    if (first === "welcome") return { name: "landing" };
    if (first === "sign-in") return { name: "signIn" };
    if (first === "sign-up") return { name: "signUp" };
    if (first === "request-access") return { name: "requestAccess" };
    if (first === "not-activated") return { name: "notActivated" };
    if (first === "new") return { name: "newProject" };
    if (first === "staff") return { name: "staff", section: "requests" };
    if (first === "settings") return { name: "settings", section: "profile" };
    return undefined;
  }
  if (first === "auth" && second === "callback") return { name: "authCallback" };
  if (first === "onboarding" && oneOf(ONBOARDING_STEPS, second))
    return { name: "onboarding", step: second };
  if (first === "invite") return { name: "invite", token: second };
  if (first === "s") return { name: "share", token: second };
  if (first === "settings" && oneOf(SETTINGS_SECTIONS, second))
    return { name: "settings", section: second };
  if (first === "staff" && oneOf(STAFF_SECTIONS, second)) return { name: "staff", section: second };
  return undefined;
}

export function parsePath(pathname: string): Route {
  const parts = segments(pathname);
  const [first, projectId, module, tab, item, ...rest] = parts;
  if (first === undefined) return { name: "projects" };
  if (first !== PROJECT_PREFIX) return parseOther(parts) ?? { name: "notFound", path: pathname };
  if (projectId === undefined || rest.length > 0) return { name: "notFound", path: pathname };
  if (module === undefined) return { name: "overview", projectId };
  if (module === "files" && tab === undefined) return { name: "files", projectId };
  if (!isModuleId(module)) return { name: "notFound", path: pathname };
  if (module === "design" && tab === DRAFT_TAB) {
    return item === undefined
      ? { name: "draft", projectId }
      : { name: "draft", projectId, drawingId: item };
  }
  return {
    name: "module",
    projectId,
    module,
    ...(tab === undefined ? {} : { tab }),
    ...(item === undefined ? {} : { item }),
  };
}

const enc = encodeURIComponent;

export function pathOf(route: Route): string {
  switch (route.name) {
    case "landing":
      return "/welcome";
    case "signIn":
      return "/sign-in";
    case "signUp":
      return "/sign-up";
    case "authCallback":
      return "/auth/callback";
    case "requestAccess":
      return "/request-access";
    case "notActivated":
      return "/not-activated";
    case "onboarding":
      return `/onboarding/${route.step}`;
    case "invite":
      return `/invite/${enc(route.token)}`;
    case "share":
      return `/s/${enc(route.token)}`;
    case "settings":
      return `/settings/${route.section}`;
    case "staff":
      return `/staff/${route.section}`;
    case "projects":
      return "/";
    case "newProject":
      return "/new";
    case "overview":
      return `/${PROJECT_PREFIX}/${enc(route.projectId)}`;
    case "files":
      return `/${PROJECT_PREFIX}/${enc(route.projectId)}/files`;
    case "draft":
      return `/${PROJECT_PREFIX}/${enc(route.projectId)}/design/${DRAFT_TAB}${route.drawingId === undefined ? "" : `/${enc(route.drawingId)}`}`;
    case "module": {
      const tail = [route.tab, route.item]
        .filter((part): part is string => part !== undefined)
        .map(enc);
      return [`/${PROJECT_PREFIX}`, enc(route.projectId), route.module, ...tail].join("/");
    }
    case "notFound":
      return route.path;
  }
}
